import React from 'react'
import styled from '@emotion/styled'
import { PrivateLessonOrder, PrivateLessonOrderStatus } from '../../../pure-js/types/GrooverTypes'
import { getDefaultInstructorName, getLocationName } from '../../../pure-js/libs/PrivateLessonHelpers'

const OrdersContainer = styled.div`
  width: 100%;
  min-width: 0;
`

const OrdersList = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 400px), 1fr));

  @media (min-width: 769px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const OrderCard = styled.div`
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.15);
  }
`

const OrderDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--portal-primary, #7c3aed);
  margin-bottom: 8px;
`

const OrderId = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  margin-bottom: 8px;
`

const OrderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const OrderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`

const OrderInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const OrderStatus = styled.div<{ $status: PrivateLessonOrderStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  margin-bottom: 12px;

  ${({ $status }) => {
    switch ($status) {
      case PrivateLessonOrderStatus.Active:
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        `
      case PrivateLessonOrderStatus.PendingPayment:
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #f59e0b;
        `
      case PrivateLessonOrderStatus.Expired:
      case PrivateLessonOrderStatus.UsedUp:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
      case PrivateLessonOrderStatus.Refunded:
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `
      default:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
    }
  }}
`

const OrderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ViewReceiptButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid var(--portal-primary, #7c3aed);
  border-radius: 6px;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  }
`

const ViewOccasionsButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    color: var(--portal-primary, #7c3aed);
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.05);
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const EmptyIcon = styled.div`
  font-size: 64px;
  color: #6b7280;
`

const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  max-width: 400px;
  margin: 0;
  line-height: 1.5;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ef4444;
`

const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  max-width: 400px;
`

// Helper functions
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatPrice = (priceInCents: number, currency?: string): string => {
  const price = priceInCents / 100
  return `${price.toFixed(2)} ${currency || 'SEK'}`
}

const ORDER_STATUS_KEYS: Record<PrivateLessonOrderStatus, string> = {
  [PrivateLessonOrderStatus.Active]: 'portalOrderStatusActive',
  [PrivateLessonOrderStatus.PendingPayment]: 'portalOrderStatusPendingPayment',
  [PrivateLessonOrderStatus.Expired]: 'portalOrderStatusExpired',
  [PrivateLessonOrderStatus.UsedUp]: 'portalOrderStatusUsedUp',
  [PrivateLessonOrderStatus.Refunded]: 'portalOrderStatusRefunded',
  [PrivateLessonOrderStatus.Deleted]: 'portalOrderStatusDeleted'
}

const getStatusLabel = (status: PrivateLessonOrderStatus, getText: (key: string) => string): string => {
  const key = ORDER_STATUS_KEYS[status]
  return key ? getText(key) : status
}

interface OrdersTabProps {
  orders: PrivateLessonOrder[] | undefined
  loading: boolean
  error: Error | undefined
  onViewReceipt: (order: PrivateLessonOrder) => void
  onViewOccasions?: (order: PrivateLessonOrder) => void
  getText: (key: string) => string
}

export const OrdersTab: React.FC<OrdersTabProps> = ({
  orders,
  loading,
  error,
  onViewReceipt,
  onViewOccasions,
  getText
}) => {
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>{getText('portalLoadingOrders')}</LoadingText>
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>{getText('portalFailedToLoadOrders')}</ErrorText>
      </ErrorContainer>
    )
  }

  if (!orders || orders.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>📦</EmptyIcon>
        <EmptyTitle>{getText('portalNoOrdersFound')}</EmptyTitle>
        <EmptyDescription>{getText('portalNoOrdersDescription')}</EmptyDescription>
      </EmptyState>
    )
  }

  return (
    <OrdersContainer>
      <OrdersList>
        {orders.map((order) => {
          const instructorName = getDefaultInstructorName(order)
          const locationName = getLocationName(order)

          return (
            <OrderCard key={order.id}>
              <OrderDate>{formatDate(order.createdAt)}</OrderDate>
              <OrderId>
                {getText('portalOrderId')}: {order.id}
              </OrderId>
              <OrderDetails>
                <OrderTitle>{order.packageName || getText('portalPrivateLessonPackage')}</OrderTitle>
                <OrderInfo>
                  {order.lessonCount} {order.lessonCount !== 1 ? getText('portalLessons') : getText('portalLesson')} (
                  {order.lessonsUsed} {getText('portalUsed')})
                </OrderInfo>
                <OrderInfo>{formatPrice(order.price, order.currency)}</OrderInfo>
                {instructorName && (
                  <OrderInfo>
                    {getText('portalOrderInstructor')} {instructorName}
                  </OrderInfo>
                )}
                {locationName && (
                  <OrderInfo>
                    {getText('portalOrderLocation')} {locationName}
                  </OrderInfo>
                )}
              </OrderDetails>
              <OrderStatus $status={order.status}>{getStatusLabel(order.status, getText)}</OrderStatus>
              <OrderActions>
                {order.status !== PrivateLessonOrderStatus.PendingPayment && (
                  <ViewReceiptButton onClick={() => onViewReceipt(order)}>
                    {getText('portalViewReceipt')}
                  </ViewReceiptButton>
                )}
                {onViewOccasions && (
                  <ViewOccasionsButton onClick={() => onViewOccasions(order)}>
                    {getText('portalViewOccasions')}
                  </ViewOccasionsButton>
                )}
              </OrderActions>
            </OrderCard>
          )
        })}
      </OrdersList>
    </OrdersContainer>
  )
}
